import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { conversationFailedText } from 'features/conversation-preferences/address-form';
import { useAddressForm } from 'features/conversation-preferences/hooks/use-conversation-preferences';
import { useToast } from 'hooks/use-toast';
import {
  AGENT_SOUL_CHAR_LIMIT,
  isSoulAccessDenied,
  isSoulTooLongError,
  soulConflictRevision,
  soulLength,
  type AgentSoul,
  type UpdateAgentSoulResponse,
} from '../services/agent-soul-service';
import {
  AGENT_SOUL_QUERY_KEY,
  isAgentSoulHidden,
  useAgentSoul,
  useUpdateAgentSoul,
} from './use-agent-soul';
import { useSoulLeaveGuard } from './use-soul-leave-guard';

/** What a refused save left behind: a 409, a 400 for the length, or a 403/404. */
type SoulProblem = 'conflict' | 'tooLong' | 'denied' | null;
export type SoulFieldError = 'tooLong' | 'empty' | null;

const SURROUNDING = new Set([' ', '\t', '\r', '\n']);

/** The backend stores the soul without surrounding spaces and line breaks (" \t\r\n"). */
export const storedContent = (content: string) => {
  let start = 0;
  let end = content.length;
  while (start < end && SURROUNDING.has(content[start])) start += 1;
  while (end > start && SURROUNDING.has(content[end - 1])) end -= 1;
  return content.slice(start, end);
};

const saveProblemOf = (error: unknown): SoulProblem | 'failed' => {
  if (soulConflictRevision(error) !== undefined) return 'conflict';
  if (isSoulTooLongError(error)) return 'tooLong';
  if (isSoulAccessDenied(error)) return 'denied';
  return 'failed';
};

const fieldErrorOf = (isTooLong: boolean, isEmpty: boolean): SoulFieldError => {
  if (isTooLong) return 'tooLong';
  return isEmpty ? 'empty' : null;
};

/**
 * State and actions of the soul editor: the draft over the loaded soul, its checks (counted as
 * the backend stores it), save and restore-default with the revision of the last load, and the
 * outcome of a refused save. A read-only editor always shows the server text, never a draft.
 */
export function useSoulEditor(agentId: string, canEdit: boolean) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const addressForm = useAddressForm();
  const { data: soul, error, isLoading, refetch } = useAgentSoul(agentId);
  const updateSoul = useUpdateAgentSoul();
  const [draft, setDraft] = useState<string | null>(null);
  const [problem, setProblem] = useState<SoulProblem>(null);
  // Set synchronously, so a second click before the re-render can't send a second request (C11).
  const inFlightRef = useRef(false);

  const isReadOnlyError = problem === 'denied' || isSoulAccessDenied(error);
  const isEditable =
    !!soul?.editable && canEdit && !isReadOnlyError && soul.source !== 'platform_buddy';
  const content = soul?.content ?? '';
  const value = isEditable ? (draft ?? content) : content;
  const hasDraft = isEditable && draft !== null;
  const isDirty = hasDraft && storedContent(draft ?? '') !== storedContent(content);
  const count = soulLength(storedContent(value));
  const isTooLong = count > (soul?.charLimit || AGENT_SOUL_CHAR_LIMIT) || problem === 'tooLong';
  // Saving nothing would remove the own soul; that is what "Restore default" is for.
  const isEmpty = hasDraft && count === 0;
  const leave = useSoulLeaveGuard(isDirty, () => setDraft(null));
  const queryKey = [AGENT_SOUL_QUERY_KEY, agentId];

  const failedText = conversationFailedText(t, addressForm);

  const handleSaveError = (saveError: unknown) => {
    const next = saveProblemOf(saveError);
    if (next === 'failed') {
      toast({ variant: 'destructive', title: failedText });
      return;
    }
    if (next === 'denied') setDraft(null);
    setProblem(next);
  };

  const persist = async (
    nextContent: string,
    afterSave: (saved: UpdateAgentSoulResponse, current: AgentSoul) => Promise<void> | void
  ) => {
    if (!soul || inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const saved = await updateSoul.mutateAsync({
        agentId,
        content: nextContent,
        expectedRevision: soul.revision,
      });
      await afterSave(saved, soul);
      setProblem(null);
      toast({ title: t('AGENT_SOUL_SAVED', { defaultValue: 'Saved.' }) });
    } catch (saveError) {
      handleSaveError(saveError);
    } finally {
      inFlightRef.current = false;
    }
  };

  const save = () => {
    if (!isDirty || isTooLong || isEmpty) return;
    const next = value;
    void persist(next, (saved, current) => {
      queryClient.setQueryData<AgentSoul>(queryKey, {
        ...current,
        content: storedContent(next),
        revision: saved.revision,
        source: saved.source,
        seeded: false,
      });
      // Text typed while the save was in flight stays as the next draft.
      setDraft((latest) => (latest === next ? null : latest));
      void queryClient.invalidateQueries({ queryKey });
    });
  };

  // H9: removing the own soul brings back the default; with none saved there is nothing to remove.
  const reset = () => {
    if (soul?.revision === null) {
      setDraft(null);
      return;
    }
    void persist('', async () => {
      await queryClient.invalidateQueries({ queryKey });
      setDraft(null);
    });
  };

  const reload = () => {
    void refetch().then(() => {
      setDraft(null);
      setProblem(null);
    });
  };

  const change = (next: string) => {
    setDraft(next);
    setProblem((current) => (current === 'tooLong' ? null : current));
  };

  return {
    soul,
    isHidden: isAgentSoulHidden(soul, error),
    isLoading,
    isReadOnlyError,
    isEditable,
    isConflict: problem === 'conflict',
    isSaving: updateSoul.isPending,
    value,
    count,
    isTooLong,
    isEmpty,
    isDirty,
    fieldError: fieldErrorOf(isTooLong, isEmpty),
    failedText,
    leave,
    retry: () => void refetch(),
    change,
    save,
    reset,
    reload,
  };
}
